import * as XLSX from 'xlsx';

/**
 * Dynamically loads SheetJS (XLSX) from a CDN to avoid bundling its large package size.
 *
 * SheetJS is a powerful but heavy library (~700KB+ uncompressed). To improve bundle performance and reduce initial
 * JavaScript payload, we load it only when needed (on demand), directly from a CDN.
 *
 * The official CDN build is a UMD module that exposes the `XLSX` object on the global `window`.
 * This function ensures it's loaded once and cached for subsequent use.
 *
 * @returns {Promise<XLSXType>} A promise that resolves with the XLSX object once the script is loaded.
 */
const loadSheetJSFromCDN = async (): Promise<typeof XLSX> => {
  if (window.XLSX) return window.XLSX; // Already loaded

  if (window.System?.import) {
    await System.import(
      'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js',
    );
  } else {
    window.XLSX = await import(
      'https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs' as string
    );
  }

  return window.XLSX;
};

export default loadSheetJSFromCDN;

declare global {
  interface Window {
    XLSX: typeof XLSX;
    System: { import: () => Promise<any> } | undefined; // For SystemJS
  }
}
